import { ExhaustChannel, ExhaustGraph } from 'icreate/common/models/graphs';

export async function printDeviationGraph(graph: ExhaustGraph, echarts, oldGraph?: ExhaustGraph) {
    const exhaustChannels = graph.CylinderChannels;
    const oldGraphChannels = oldGraph ? (oldGraph.CylinderChannels ?? []) : null;

    if (!exhaustChannels || exhaustChannels.length === 0) {
        return;
    }

    const echartsInstance = echarts.default || echarts;

    const averageChannelTag = graph.averageChannelData?.fcuData?.fcuChannelId ?? '';

    const maxLimit = findMaxLimit(exhaustChannels);
    const minLimit = findMinLimit(exhaustChannels);

    let maxVal = Number(maxLimit);
    let minVal = Number(minLimit);
    if (isNaN(maxVal)) maxVal = 50;
    if (isNaN(minVal)) minVal = -50;

    let deviationLimit = Math.max(Math.abs(maxVal), Math.abs(minVal));
    if (deviationLimit === 0 || isNaN(deviationLimit)) {
        deviationLimit = 50;
    }

    const yAxisDecimalPlaces = findMaxDecimalPlaces(exhaustChannels);

    const { notBorderedLables, borderedLables, changedIndexes } = prepareXAxislabel(exhaustChannels, oldGraphChannels);

    const channelValues = generateChannelValues(exhaustChannels);
    const valueLabels = prepareValueLabels(channelValues);
    const graphValuesArray = prepareGraphValuesArray(channelValues);

    const titleBorder =
        oldGraph && oldGraph?.title != graph.title ? [true, true, true, true] : [false, false, false, false];
    const averageChannelTagTextStyle =
        oldGraph && oldGraph?.averageChannel?.fcuData?.fcuChannelId !== graph.averageChannel?.fcuData?.fcuChannelId
            ? {
                  borderColor: 'red',
                  borderWidth: 1,
                  padding: [2, 2, 2, 2],
              }
            : {};

    const barGraphDivision = document.createElement('div');
    barGraphDivision.style.height = '490pt';
    barGraphDivision.style.width = '600pt';

    const myChart = echartsInstance.init(barGraphDivision, null, { width: 600, height: 490 });

    const calculatedInterval = deviationLimit / 4;
    const safeInterval = calculatedInterval > 0 ? calculatedInterval : 10;

    const option = {
        color: ['black'],
        grid: {
            bottom: 60,
            right: 10,
        },
        xAxis: [
            {
                type: 'category',
                data: notBorderedLables.data,
                position: 'bottom',
                axisTick: { show: false },
            },
            {
                type: 'category',
                data: borderedLables.data,
                position: 'bottom',
                axisTick: { show: false },
            },
            {
                type: 'category',
                data: valueLabels.data,
                position: 'bottom',
                offset: 22,
                axisLine: { show: false },
                axisTick: { show: false },
                axisLabel: { formatter: () => '' },
            },
        ],
        yAxis: [
            {
                type: 'value',
                max: deviationLimit,
                min: -deviationLimit,
                interval: safeInterval,
                axisLine: { lineStyle: { color: 'black' } },
                splitLine: { lineStyle: { color: 'black' } },
                axisLabel: {
                    formatter: function (value: number) {
                        if (value === 0) return '';
                        return value.toFixed(yAxisDecimalPlaces);
                    },
                },
            },
            {
                type: 'value',
                max: deviationLimit,
                min: -deviationLimit,
                interval: safeInterval,
                axisLine: { lineStyle: { color: 'black' } },
                splitLine: { lineStyle: { color: 'black' } },
                position: 'left',
                axisLabel: {
                    customValues: [0],
                    formatter: function () {
                        return `AVE \n ***** \n (${averageChannelTag})`;
                    },
                    textStyle: averageChannelTagTextStyle,
                },
            },
        ],
        series: [
            {
                data: graphValuesArray,
                type: 'bar',
            },
        ],
    };

    myChart.setOption(option);

    await new Promise(resolve => setTimeout(resolve, 500));

    const dataUrl = myChart.getDataURL({
        type: 'png',
        pixelRatio: 1,
        backgroundColor: '#fff',
    });

    const legendTable = buildLegendTable(exhaustChannels, changedIndexes);

    return {
        body: [
            [
                [
                    {
                        columns: [
                            { width: '*', text: '' },
                            {
                                width: 'auto',
                                table: {
                                    body: [
                                        [
                                            {
                                                text: graph.title,
                                                border: titleBorder,
                                                borderColor: ['red', 'red', 'red', 'red'],
                                                fontSize: 15,
                                                alignment: 'center',
                                                noWrap: true,
                                            },
                                        ],
                                    ],
                                },
                            },
                            { width: '*', text: '' },
                        ],
                        margin: [0, 10, 0, 10],
                    },
                    {
                        columnGap: 8,
                        columns: [
                            {
                                width: 'auto',
                                table: {
                                    body: [
                                        [
                                            {
                                                image: dataUrl,
                                                width: 655,
                                                height: 440,
                                                border: [false, false],
                                            },
                                        ],
                                    ],
                                    alignment: 'center',
                                },
                                layout: {
                                    paddingLeft: () => 0,
                                    paddingRight: () => 0,
                                    paddingTop: () => 0,
                                    paddingBottom: () => 0,
                                },
                            },
                            {
                                width: 'auto',
                                table: legendTable,
                                layout: {
                                    defaultBorder: false,
                                    paddingLeft: () => 4,
                                    paddingRight: () => 4,
                                    paddingTop: () => 2,
                                    paddingBottom: () => 2,
                                },
                            },
                        ],
                    },
                ],
            ],
        ],
        widths: [763],
        heights: [490],
    };
}

function prepareXAxislabel(channels: ExhaustChannel[], oldChannels?: ExhaustChannel[]) {
    let newChannelsIds = new Set();
    let deletedChannelsLength = 0;

    let borderedLables = { data: [] as string[], customValues: [] as number[] };
    let notBorderedLables = { data: [] as string[], customValues: [] as number[] };
    const changedIndexes: Set<number> = new Set();

    if (oldChannels) {
        const oldChannelsArray = oldChannels.map(channel => channel?.deviationChannel);
        const oldChannelsSet = new Set(oldChannelsArray);

        channels.forEach(channel => {
            const fcuChannelId = channel?.deviationChannel?.fcuData?.fcuChannelId;
            if (fcuChannelId && !oldChannelsSet.has(channel?.deviationChannel)) newChannelsIds.add(fcuChannelId);
        });

        deletedChannelsLength = Math.max(oldChannels.length - channels.length, 0);
    }

    channels.forEach((channel: ExhaustChannel, index) => {
        const fcuChannelId = channel?.deviationChannel?.fcuData?.fcuChannelId;
        const position = String(index + 1);
        if (fcuChannelId && newChannelsIds.has(fcuChannelId)) {
            borderedLables.data.push(position);
            borderedLables.customValues.push(index);
            notBorderedLables.data.push('');
            changedIndexes.add(index);
        } else {
            notBorderedLables.data.push(position);
            notBorderedLables.customValues.push(index);
            borderedLables.data.push('');
        }
    });

    for (let i = 0; i < deletedChannelsLength; i++) {
        notBorderedLables.data.push('');
        borderedLables.data.push(String(channels.length + i + 1));
        borderedLables.customValues.push(channels.length + i);
    }

    return { borderedLables, notBorderedLables, changedIndexes };
}

function buildLegendTable(channels: ExhaustChannel[], changedIndexes: Set<number>) {
    const border = [true, true, true, true] as [boolean, boolean, boolean, boolean];
    const firstValueChanged = changedIndexes.has(0);
    const borderColor = ['black', 'black', 'black', firstValueChanged ? 'red' : 'black'] as [
        string,
        string,
        string,
        string,
    ];
    const header = [
        { text: '#', fontWeight: 'bold', border, borderColor },
        { text: 'Ch. Tag.', fontWeight: 'bold', border, borderColor },
    ];

    const rows = channels.map((channel, index) => {
        const isChanged = changedIndexes.has(index);
        const topRowChanged = changedIndexes.has(index - 1);
        const bottomRowChanged = changedIndexes.has(index + 1);

        const topRowColor = isChanged || topRowChanged ? 'red' : 'black';
        const bottomRowColor = isChanged || bottomRowChanged ? 'red' : 'black';
        const rowBorderColor = [
            isChanged ? 'red' : 'black',
            topRowColor,
            isChanged ? 'red' : 'black',
            bottomRowColor,
        ] as [string, string, string, string];

        return [
            { text: String(index + 1), border, borderColor: rowBorderColor },
            { text: channel?.deviationChannel?.fcuData?.fcuChannelId ?? '', border, borderColor: rowBorderColor },
        ];
    });

    return {
        widths: [10, 50],
        body: [header, ...rows],
    };
}

function getDecimalPlaces(channel: ExhaustChannel): number {
    const scale = channel?.deviationChannel?.fcuData?.scale || 1;
    return Math.max(Math.round(Math.log10(scale)), 0);
}

function findMaxDecimalPlaces(exhaustChannels: ExhaustChannel[]): number {
    return exhaustChannels.reduce((max, channel) => Math.max(max, getDecimalPlaces(channel)), 0);
}

function findMaxLimit(exhaustChannels: ExhaustChannel[]): string {
    const maxValues = exhaustChannels.map((channel: ExhaustChannel) => {
        const scale = channel?.deviationChannel?.fcuData?.scale || 1;
        const maxScaled = (channel?.deviationChannel?.fcuData?.measurableRange?.max || 50) / scale;
        return maxScaled;
    });

    const sortedNumbers = maxValues.sort((a, b) => b - a);
    return String(sortedNumbers[0] ?? 50);
}

function findMinLimit(graphChannels: ExhaustChannel[]): string {
    const minValues = graphChannels.map((channel: ExhaustChannel) => {
        const scale = channel?.deviationChannel?.fcuData?.scale || 1;
        const minScaled = (channel?.deviationChannel?.fcuData?.measurableRange?.min || -50) / scale;
        return minScaled;
    });

    const sortedNumbers = minValues.sort((a, b) => a - b);
    return String(sortedNumbers[0] ?? -50);
}

function generateChannelValues(exhaustChannels: ExhaustChannel[]) {
    return exhaustChannels.map((channel: ExhaustChannel) => {
        if (!channel?.deviationChannel) return { channel: null, value: 0 };
        const measurableRange = channel?.deviationChannel?.fcuData?.measurableRange;
        const min = measurableRange?.min ?? -50;
        const max = measurableRange?.max ?? 50;
        const scale = channel?.deviationChannel?.fcuData?.scale || 1;
        const decimalPlaces = getDecimalPlaces(channel);

        const randomRaw = Math.floor(Math.random() * (max - min + 1)) + min;
        const scaledValue = Number((randomRaw / scale).toFixed(decimalPlaces));

        return { channel, value: isNaN(scaledValue) ? 0 : scaledValue };
    });
}

function prepareValueLabels(channelValues: { channel: ExhaustChannel; value: number }[]) {
    return {
        data: channelValues.map(({ channel, value }) =>
            value && channel ? value.toFixed(getDecimalPlaces(channel)) : ''
        ),
    };
}

function prepareGraphValuesArray(channelValues: { channel: ExhaustChannel; value: number }[]) {
    return channelValues.map(({ value }) => (isNaN(value) ? 0 : value));
}
